import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Mail, Building2, Paperclip, X, Download, FileText } from "lucide-react";

function formatTime(iso) {
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

export default function SeekerMessages() {
    const { threadId: deepLinkThreadId } = useParams();
    const [threads, setThreads] = useState([]);
    const [activeId, setActiveId] = useState(deepLinkThreadId || null);
    const [thread, setThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [loadingList, setLoadingList] = useState(true);
    const [loadingThread, setLoadingThread] = useState(false);
    const bottomRef = useRef(null);
    const pollRef = useRef(null);
    // Phase 152 — pending attachments uploaded by the candidate for the next reply
    const [pending, setPending] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const pickFiles = async (files) => {
        if (!files?.length || !activeId) return;
        setUploading(true);
        try {
            const out = [];
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                fd.append("thread_id", activeId);
                const { data } = await api.post("/messages/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
                out.push(data);
            }
            setPending((p) => [...p, ...out]);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const removePending = (id) => setPending((p) => p.filter((x) => x.id !== id));

    const downloadAttachment = async (att) => {
        try {
            const r = await api.get(`/messages/attachments/${att.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = att.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const loadThreads = async () => {
        setLoadingList(true);
        try {
            const { data } = await api.get("/messages/threads");
            setThreads(data || []);
            if (!activeId && (data || []).length) setActiveId(deepLinkThreadId || data[0].id);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoadingList(false); }
    };

    const loadThread = async (id) => {
        if (!id) return;
        setLoadingThread(true);
        try {
            const { data } = await api.get(`/messages/threads/${id}`);
            setThread(data);
            setMessages(data.messages || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoadingThread(false); }
    };

    useEffect(() => { loadThreads(); /* eslint-disable-next-line */ }, []);
    useEffect(() => { loadThread(activeId); /* eslint-disable-next-line */ }, [activeId]);

    useEffect(() => {
        if (!activeId) return;
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/messages/threads/${activeId}`);
                setMessages(data.messages || []);
            } catch { /* ignore */ }
        }, 8000);
        return () => clearInterval(pollRef.current);
    }, [activeId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages.length]);

    const send = async (e) => {
        e?.preventDefault?.();
        const body = draft.trim();
        if ((!body && pending.length === 0) || !activeId) return;
        setSending(true);
        try {
            const attachment_ids = pending.map((p) => p.id);
            const { data: msg } = await api.post(`/messages/threads/${activeId}/reply`, {
                body: body || "(sent an attachment)",
                attachment_ids,
            });
            setMessages((m) => [...m, msg]);
            setDraft("");
            setPending([]);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSending(false); }
    };

    return (
        <div className="max-w-6xl mx-auto p-3 sm:p-6 lg:p-8">
            <div className="mb-6">
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">Inbox</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">Messages from recruiters</h1>
                <p className="text-muted-foreground mt-2">Companies you've applied to may reach out here. Replies go directly back to their hiring team.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-border border border-border min-h-[60vh]">
                <aside className="md:col-span-4 bg-white overflow-y-auto max-h-[70vh]">
                    {loadingList ? (
                        <div className="p-6 text-xs text-muted-foreground font-mono">Loading...</div>
                    ) : threads.length === 0 ? (
                        <div className="p-6 text-sm text-muted-foreground text-center">
                            <Mail className="w-6 h-6 mx-auto mb-2 text-muted-foreground/50" />
                            No conversations yet. Recruiters from companies you applied to will message you here.
                        </div>
                    ) : (
                        threads.map((t) => {
                            const active = t.id === activeId;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => setActiveId(t.id)}
                                    data-testid={`seeker-thread-${t.id}`}
                                    className={`w-full text-left p-4 border-b border-border transition-colors ${active ? "bg-blue-50/60 border-l-2 border-l-ikb" : "hover:bg-secondary border-l-2 border-l-transparent"}`}
                                >
                                    <div className="font-medium text-sm flex items-center gap-2">
                                        <Building2 className="w-3.5 h-3.5 text-ikb shrink-0" />
                                        <span className="truncate">{t.company?.name || "Hiring team"}</span>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1 truncate">{t.subject || "Conversation"}</div>
                                    <div className="text-[10px] text-muted-foreground font-mono mt-1">{formatTime(t.last_message_at || t.created_at)}</div>
                                </button>
                            );
                        })
                    )}
                </aside>

                <section className="md:col-span-8 bg-white flex flex-col max-h-[70vh]">
                    {!activeId ? (
                        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                            Select a conversation to read messages.
                        </div>
                    ) : (
                        <>
                            <div className="p-4 border-b border-border">
                                <div className="font-display font-bold text-base flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-ikb" /> {thread?.company?.name || "Hiring team"}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">{thread?.subject}</div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-5 bg-secondary/40 space-y-3" data-testid="seeker-message-list">
                                {loadingThread ? (
                                    <div className="text-xs text-muted-foreground font-mono">Loading...</div>
                                ) : messages.map((m) => {
                                    const mine = m.sender_role === "seeker";
                                    return (
                                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-[80%] border px-3 py-2 ${mine ? "bg-ikb text-white border-ikb" : "bg-white border-border"}`}>
                                                <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                                                    {m.sender_name || (mine ? "You" : "Recruiter")} · {formatTime(m.created_at)}
                                                </div>
                                                <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                                                {(m.attachments || []).length > 0 && (
                                                    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`seeker-msg-attachments-${m.id}`}>
                                                        {m.attachments.map((a) => (
                                                            <button
                                                                key={a.id}
                                                                type="button"
                                                                onClick={() => downloadAttachment(a)}
                                                                className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border rounded-md max-w-[220px] truncate ${mine ? "border-white/40 hover:bg-white/10" : "border-border hover:bg-secondary/80"}`}
                                                                title={`${a.filename} · ${Math.round((a.size || 0) / 1024)} KB`}
                                                                data-testid={`seeker-msg-attachment-${a.id}`}
                                                            >
                                                                <FileText className="w-3 h-3 shrink-0" />
                                                                <span className="truncate">{a.filename}</span>
                                                                <Download className="w-3 h-3 shrink-0 opacity-70" />
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={bottomRef} />
                            </div>
                            <form onSubmit={send} className="border-t border-border p-4">
                                <Textarea
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); }}
                                    placeholder="Write a reply...  (Cmd/Ctrl+Enter to send)"
                                    rows={2}
                                    className="rounded-none resize-none"
                                    data-testid="seeker-message-input"
                                />
                                {pending.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="seeker-msg-pending-attachments">
                                        {pending.map((p) => (
                                            <span key={p.id} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 bg-secondary border border-border rounded-md">
                                                <FileText className="w-3 h-3" />
                                                <span className="max-w-[160px] truncate">{p.filename}</span>
                                                <button type="button" onClick={() => removePending(p.id)} className="hover:text-destructive" aria-label="Remove" data-testid={`seeker-msg-remove-attachment-${p.id}`}>
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="flex items-center justify-between mt-3">
                                    <input ref={fileRef} type="file" hidden multiple onChange={(e) => pickFiles(e.target.files)} data-testid="seeker-msg-attach-input" />
                                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-none" data-testid="seeker-msg-attach-btn">
                                        <Paperclip className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : "Attach"}
                                    </Button>
                                    <Button type="submit" disabled={sending || (!draft.trim() && pending.length === 0)} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="seeker-message-send">
                                        <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : "Send reply"}
                                    </Button>
                                </div>
                            </form>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}
